import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Flag, Loader2, Plus, X } from "lucide-react";
import { BudgetDialog, CycleBudgetBar } from "./CycleBudget";

const STAGE_LABEL = {
  LIVE_PRETEST: "Pré-Teste",
  LIVE_TEST: "Teste",
  LIVE_PRESCALE: "Pré-Escala",
  LIVE_SCALE: "Escala",
};

const STAGE_TONE = {
  LIVE_PRETEST: "bg-slate-500/15 border-slate-400/40 text-slate-200",
  LIVE_TEST: "bg-blue-500/15 border-blue-400/40 text-blue-200",
  LIVE_PRESCALE: "bg-amber-500/15 border-amber-400/40 text-amber-200",
  LIVE_SCALE: "bg-emerald-500/15 border-emerald-400/40 text-emerald-200",
};

function OpenCycleDialog({ open, onClose, onConfirm, busy }) {
  const [name, setName] = useState("");
  const [budget, setBudget] = useState("");
  useEffect(() => { if (open) { setName(""); setBudget(""); } }, [open]);
  const parsedBudget = parseFloat(String(budget).replace(",", "."));
  if (!open) return null;
  return (
    <div
      data-testid="open-cycle-dialog-overlay"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="bg-[#14161C] border border-[#282D3D] rounded-2xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-extrabold text-base">Iniciar novo ciclo</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <label className="block text-[11px] font-bold text-gray-300 mb-1">
          Nome (ex: "Conta 03")
        </label>
        <input
          data-testid="open-cycle-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Conta 01"
          className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white text-sm"
        />
        <label className="block text-[11px] font-bold text-gray-300 mb-1 mt-3">
          Orçamento dedicado ao ciclo (R$) — opcional
        </label>
        <input
          data-testid="open-cycle-budget-input"
          inputMode="decimal"
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          placeholder="Ex: 500,00"
          className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white text-sm"
        />
        <p className="text-[11px] text-gray-500 mt-2 leading-snug">
          O Conselheiro usa o orçamento para ajustar as sugestões. Começa em <strong>Pré-Teste</strong>. Outros ciclos ativos continuam
          rodando normalmente.
        </p>
        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg bg-[#1F2330] border border-[#3E4760] text-gray-300 font-bold text-xs uppercase"
          >
            Cancelar
          </button>
          <button
            data-testid="open-cycle-confirm-btn"
            onClick={() => onConfirm(name.trim() || null, parsedBudget > 0 ? parsedBudget : null)}
            disabled={busy}
            className="flex-1 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Iniciar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LiveCycleControls({ onChange, version }) {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [budgetCycle, setBudgetCycle] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/api/live-cycles/active");
      setCycles(res.data?.active_cycles || []);
    } catch (_err) {
      setCycles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh, version]);

  const callOpen = async (name, budget) => {
    setBusy(true);
    try {
      await api.post("/api/live-cycles/open", { ...(name ? { name } : {}), ...(budget ? { budget } : {}) });
      setOpenDialog(false);
      await refresh();
      onChange?.();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Falha ao iniciar ciclo.");
    } finally {
      setBusy(false);
    }
  };

  const callClose = async (cycle) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Encerrar ${cycle.name || `Ciclo #${cycle.cycle_number}`}? Os números do Resumo deste ciclo voltam a zero (o histórico fica salvo). Os outros ciclos ativos permanecem rodando.`)) return;
    setBusy(true);
    try {
      await api.post(`/api/live-cycles/${cycle.id}/close`, {});
      await refresh();
      onChange?.();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Falha ao encerrar ciclo.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  return (
    <>
      <div
        data-testid="live-cycle-controls"
        className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-bold text-[#FF6B00] uppercase tracking-wider">
            Ciclos Live Ativos
            <span data-testid="active-cycles-count" className="ml-1 text-gray-400 font-normal">
              ({cycles.length})
            </span>
          </div>
          <button
            data-testid="open-cycle-btn"
            onClick={() => setOpenDialog(true)}
            disabled={busy}
            className="py-1 px-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-[11px] uppercase flex items-center gap-1 disabled:opacity-60"
          >
            <Plus className="w-3.5 h-3.5" /> Novo ciclo
          </button>
        </div>

        {cycles.length === 0 ? (
          <div data-testid="no-active-cycle-label" className="text-gray-300 text-sm">
            Nenhum ciclo ativo. Inicie um ciclo para registrar leads/vendas.
          </div>
        ) : (
          <div className="space-y-2">
            {cycles.map((c) => {
              const label = STAGE_LABEL[c.stage] || c.stage;
              const tone = STAGE_TONE[c.stage] || STAGE_TONE.LIVE_PRETEST;
              return (
                <div
                  key={c.id}
                  data-testid={`active-cycle-${c.cycle_number}`}
                  className="p-2.5 rounded-lg bg-[#0F1218] border border-[#282D3D]"
                >
                  <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-white font-extrabold text-sm truncate">
                      {c.name || `Ciclo #${c.cycle_number}`}
                      <span className="ml-2 text-[11px] text-gray-500 font-normal">
                        #{c.cycle_number}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span
                        data-testid={`cycle-stage-${c.cycle_number}`}
                        className={`text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${tone}`}
                      >
                        {label}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {c.total_leads}L / {c.total_reached_values}V / {c.total_sales}V$ /{" "}
                        R$ {Number(c.total_spend || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    data-testid={`close-cycle-${c.cycle_number}`}
                    onClick={() => callClose(c)}
                    disabled={busy}
                    title="Encerrar ciclo"
                    className="shrink-0 py-1 px-2 rounded-lg bg-[#1F2330] hover:bg-red-500/20 hover:text-red-300 border border-[#3E4760] text-gray-300 font-bold text-[10px] uppercase flex items-center gap-1 disabled:opacity-60"
                  >
                    <Flag className="w-3 h-3" /> Encerrar
                  </button>
                  </div>
                  <CycleBudgetBar
                    cycle={c}
                    onEdit={() => setBudgetCycle(c)}
                    onChanged={() => { refresh(); onChange?.(); }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <OpenCycleDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={callOpen}
        busy={busy}
      />
      <BudgetDialog
        cycle={budgetCycle}
        onClose={() => setBudgetCycle(null)}
        onSaved={() => { refresh(); onChange?.(); }}
      />
    </>
  );
}
